import React, { useState, useEffect } from "react";
import { FaDownload, FaTimes, FaShareSquare } from "react-icons/fa";

const DISMISSED_KEY = "installPromptDismissed";

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    // Already dismissed by user
    if (localStorage.getItem(DISMISSED_KEY)) return;

    // Already running as an installed PWA — don't show
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone) return;

    // iOS Safari detection (no beforeinstallprompt support — needs manual steps)
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIos && isSafari) {
      setShowIos(true);
      return;
    }

    // Android / Chrome — capture the native install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroid(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowAndroid(false);
    // If accepted, don't bother them again
    if (outcome === "accepted") {
      localStorage.setItem(DISMISSED_KEY, "1");
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setShowAndroid(false);
    setShowIos(false);
  };

  if (!showAndroid && !showIos) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white px-4 py-4 shadow-2xl">
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-white p-1"
      >
        <FaTimes />
      </button>

      {/* Android / Chrome banner */}
      {showAndroid && (
        <div className="flex items-center gap-3 pr-6">
          <FaDownload className="text-blue-400 text-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Add to Home Screen</p>
            <p className="text-xs text-gray-400 truncate">
              Install the app for quick access — no app store needed
            </p>
          </div>
          <button
            onClick={handleInstall}
            className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            Install
          </button>
        </div>
      )}

      {/* iOS Safari instructions */}
      {showIos && (
        <div className="pr-6">
          <p className="font-semibold text-sm mb-1">Add to Home Screen</p>
          <p className="text-xs text-gray-300 leading-relaxed">
            Tap the{" "}
            <FaShareSquare className="inline mx-1 text-blue-400" />
            <strong>Share</strong> button in Safari, then select{" "}
            <strong>"Add to Home Screen"</strong>.
          </p>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
